import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ResPillComponent, ResPillStatus } from '../../../components/res-pill/res-pill.component';
import { ResultStatus, RunnerCase, RunnerStep } from '../../../models/test-run.model';
import { isFinalStatus } from '../../../services/execution-job.service';
import { formatDurationMs } from '../../../shared/duration.util';
import { isOpenStatus } from '../../../shared/test-run-outcome';
import { runnerPillStatus, runnerResultKey } from '../runner-status';
import { NumberedStep, numberSteps } from '../step-numbering';
import { callReason } from '../call-reason';
import { ResultNode, ResultRowKind, isStructured, passLabel, resultRows } from '../result-rows';
import { pathSegments, pathText } from '../step-path';

/** Was die Zustandsspalte zeigt: eine Pille, auch für „Wartet" (NEX-1897). */
export interface RunnerCaseState {
  pill: { status: ResPillStatus; label: string; pulsing: boolean };
  running: boolean;
  done: boolean;
  error: string | null;
}

interface StepView {
  id: string;
  number: string;
  depth: number;
  title: string;
  /** {@code null} bei einer Gruppenzeile — sie traegt kein eigenes Ergebnis. */
  pill: { status: ResPillStatus; label: string; pulsing: boolean } | null;
  duration: string;
  step: RunnerStep;
  /** Nur in Laeufen mit Aufruf-Zustand (NEX-2282); im Bestand {@code null}. */
  kind: ResultRowKind | null;
  level: number;
  pass: number | null;
  /** Datenblattzeile des Schleifenaufrufs an einer Runde. */
  row: number | null;
}

interface CaseView {
  id: string;
  key: string;
  title: string;
  state: RunnerCaseState;
  duration: string;
  runnerName: string;
  steps: StepView[];
  /** Der aktuelle Schritt des laufenden Falls; {@code null} sonst. */
  current: RunnerStep | null;
}

const NONE = '—';

function resultPill(status: ResultStatus): RunnerCaseState['pill'] {
  return { status: runnerPillStatus(status), label: runnerResultKey(status), pulsing: false };
}

const WAITING_PILL: RunnerCaseState['pill'] = { status: 'waiting', label: 'testRunner.cases.waiting', pulsing: false };

/**
 * Der Zustand eines Falls im Rechner-Lauf. Der Auftrag des Rechners fuehrt, solange es ihn gibt;
 * ein Fall ohne Auftrag (Lauf vor NEX-1752) zeigt sein eigenes Ergebnis.
 */
export function runnerCaseState(runnerCase: RunnerCase): RunnerCaseState {
  const job = runnerCase.runnerJob;
  if (!job) {
    const open = isOpenStatus(runnerCase.status);
    return { pill: open ? WAITING_PILL : resultPill(runnerCase.status), running: false, done: !open, error: null };
  }
  const done = isFinalStatus(job.status);
  switch (job.status) {
    case 'QUEUED':
      return { pill: WAITING_PILL, running: false, done, error: null };
    case 'RUNNING':
      return {
        pill: { status: 'running', label: 'testCases.run.jobStatus.RUNNING', pulsing: true },
        running: true,
        done,
        error: null,
      };
    case 'ERROR':
      return {
        pill: { status: 'failed', label: 'testCases.run.jobStatus.ERROR', pulsing: false },
        running: false,
        done,
        error: job.error,
      };
    case 'CANCELLED':
      return {
        pill: { status: 'skipped', label: 'testCases.run.jobStatus.CANCELLED', pulsing: false },
        running: false,
        done,
        error: null,
      };
    default:
      // Der Endstand des Auftrags kommt vor dem Ergebnis des Falls an.
      return {
        pill: resultPill(isOpenStatus(runnerCase.status) ? job.status : runnerCase.status),
        running: false,
        done,
        error: null,
      };
  }
}

function caseDuration(runnerCase: RunnerCase, state: RunnerCaseState): string {
  if (!state.done) return NONE;
  const job = runnerCase.runnerJob;
  const start = Date.parse((job ? job.startedAt : runnerCase.startedAt) ?? '');
  const end = Date.parse((job ? job.finishedAt : runnerCase.completedAt) ?? '');
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return NONE;
  return formatDurationMs(end - start);
}

function currentStep(numbered: readonly NumberedStep[], running: boolean): RunnerStep | null {
  if (!running) return null;
  return numbered.find((entry) => !entry.isGroup && isOpenStatus(entry.step.actualStatus))?.step ?? null;
}

function nodeOf(entry: NumberedStep): ResultNode {
  const { step } = entry;
  return {
    id: step.id,
    parentId: entry.parentId,
    kind: step.kind,
    callState: step.callState,
    passIndex: step.passIndex,
    loopPhase: step.loopPhase,
    iterationIndex: step.iterationIndex,
  };
}

function stepView(
  { step, number, depth, isGroup }: NumberedStep,
  current: RunnerStep | null,
  row: { kind: ResultRowKind; level: number; pass: number | null; row: number | null } | null,
): StepView {
  const header = row ? row.kind !== 'step' && row.kind !== 'call' : isGroup;
  return {
    id: step.id,
    number,
    depth,
    title: step.title ?? step.description ?? NONE,
    pill: header
      ? null
      : step.id === current?.id
        ? { status: 'running', label: runnerResultKey('RUNNING'), pulsing: true }
        : resultPill(step.actualStatus),
    duration: header || row?.kind === 'call' ? '' : formatDurationMs(step.durationMs),
    step,
    kind: row?.kind ?? null,
    level: row?.level ?? depth,
    pass: row?.pass ?? null,
    row: row?.row ?? null,
  };
}

/** Laeufe mit Aufruf-Zustand zeigen Runden, Rückweg und nicht ausgefuehrte Aufrufe wie die Ergebnisse (NEX-2282). */
function stepViews(numbered: NumberedStep[], current: RunnerStep | null): StepView[] {
  if (!isStructured(numbered, nodeOf)) return numbered.map((entry) => stepView(entry, current, null));
  return resultRows(numbered, nodeOf).map((row) => stepView(row.item, current, row));
}

let nextListId = 0;

/**
 * Die Faelle eines Rechner-Laufs mit ihren Schritten (NEX-1754). Rein darstellend: den Stand
 * fuehrt die Live-Ansicht fort; hier liegt nur, welche Faelle die Person auf- oder zugeklappt hat.
 */
@Component({
  selector: 'app-runner-case-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslatePipe, ResPillComponent],
  templateUrl: './runner-case-list.component.html',
  styleUrl: './runner-case-list.component.scss',
})
export class RunnerCaseListComponent {
  readonly cases = input.required<RunnerCase[]>();

  private readonly translate = inject(TranslateService);

  protected readonly idPrefix = `rcl-${nextListId++}`;

  /** Die Wahl der Person je Fall; ohne Wahl ist nur der laufende Fall offen. */
  private readonly choices = signal<ReadonlyMap<string, boolean>>(new Map());

  protected readonly views = computed<CaseView[]>(() =>
    this.cases().map((runnerCase) => {
      const state = runnerCaseState(runnerCase);
      const numbered = numberSteps(runnerCase.steps);
      const current = currentStep(numbered, state.running);
      return {
        id: runnerCase.id,
        key: runnerCase.key ?? NONE,
        title: runnerCase.title ?? NONE,
        state,
        duration: caseDuration(runnerCase, state),
        runnerName: runnerCase.runnerJob?.runnerName ?? NONE,
        steps: stepViews(numbered, current),
        current,
      };
    }),
  );

  protected readonly done = computed(() => this.views().filter((view) => view.state.done).length);

  protected isExpanded(view: CaseView): boolean {
    return this.choices().get(view.id) ?? view.state.running;
  }

  protected toggle(view: CaseView): void {
    const open = !this.isExpanded(view);
    this.choices.update((choices) => new Map(choices).set(view.id, open));
  }

  /** „TC05 › TM_R_Selbst (Runde 2) › TM_Z" fuer den aktuellen Schritt des laufenden Falls. */
  protected currentPath(view: CaseView, current: RunnerStep): string {
    const runnerCase = this.cases().find((c) => c.id === view.id);
    return pathText(pathSegments(runnerCase?.title ?? null, runnerCase?.key ?? null, current.path), this.translate);
  }

  protected passLabelOf(view: StepView): string {
    return view.kind ? passLabel({ kind: view.kind, pass: view.pass, row: view.row }, this.translate) : '';
  }

  protected reasonOf(step: RunnerStep): string {
    return callReason(step.callState, step.note, step.callConditions, this.translate);
  }

  protected rowTestId(view: StepView): string {
    switch (view.kind) {
      case 'pass':
        return 'runner-case-pass-' + view.pass;
      case 'returnPass':
        return 'runner-case-return-pass-' + view.pass;
      case 'return':
        return 'runner-case-return-block';
      case 'call':
        return 'call-skipped';
      default:
        return 'runner-case-step-row';
    }
  }

  protected stepsId(view: CaseView): string {
    return `${this.idPrefix}-steps-${view.id}`;
  }
}
